import { useCallback, useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Grid } from '@mui/system';
import { OTHER } from 'common/constants/process-management';
import { useStore } from 'common/store';
import { RequestedFileExtended } from 'common/types/files';
import { UseStoreValues } from 'common/types/store';
import { TaskContext } from 'context/TaskContext';
import { TaskContextType } from 'context/TaskContext/types';
import ProcessManagementRequestHeader from 'pages/ProcessManagement/ProcessManagementRequestHeader/ProcessManagementRequestHeader';
import { DocumentUploadModal } from 'pages/RequestDocuments/components/DocumentUploadModal';
import HowDoIUseThisDocumentsUploadSection from 'pages/RequestDocuments/components/HowDoIUseThisDocumentsUploadSection/HowDoIUseThisDocumentsUploadSection';
import SelectionModal from 'pages/RequestDocuments/components/SelectionModal/SelectionModal';
import { useDocuments } from 'services/hooks';
import { ProcessManagementDocumentUploadsProps } from './types';
import ProcessManagementDocumentList from '../ProcessManagementDocumentList/ProcessManagementDocumentList';

const ProcessManagementDocumentUploads = ({ pairedQuestionnaire }: ProcessManagementDocumentUploadsProps) => {
  const [progress, setProgress] = useState(0);
  const [openSelectionModal, setOpenSelectionModal] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<RequestedFileExtended[]>([]);
  const [documentsToShow, setDocumentsToShow] = useState<RequestedFileExtended[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<RequestedFileExtended[]>([]);
  const [showUploadBox, setShowUploadBox] = useState(true);
  const { task, setCompanyDocuments, companyDocuments, validateToken, requestedFiles } = useContext(
    TaskContext
  ) as TaskContextType;
  const { isUploading, uploadRequestedFiles, getDocuments } = useDocuments();
  const { company_id: companyId, measurement_date: md } = task.task;
  const { id: mdId } = md || {};
  const [{ user }] = useStore() as unknown as UseStoreValues;

  const handleGetDocuments = async () => {
    // This function is responsible for obtaining the documents of the company that are in its documents section
    if (!mdId || !companyId) {
      return;
    }
    try {
      const documents = (await getDocuments(mdId, companyId)) ?? [];
      setCompanyDocuments(documents[0]?.files);
    } catch (e) {
      throw new Error(String(e));
    }
  };

  const uploadToOtherRequestedFile = useCallback(() => {
    const otherRequestedFile = requestedFiles.find((item: RequestedFileExtended) => item.name === OTHER) || {
      id: OTHER,
      name: OTHER,
    };
    const requestedFilesIds = [otherRequestedFile.id];
    filesToUpload.forEach(async fileToUpload => {
      await uploadRequestedFiles({
        droppedFile: fileToUpload.file,
        requestedFilesIds,
        task,
        setUploadedFiles,
        files: companyDocuments,
      });
      validateToken();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToUpload, requestedFiles, setUploadedFiles, task, uploadRequestedFiles]);

  useEffect(() => {
    if (user) {
      handleGetDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles, user]);

  useEffect(() => {
    const answered = requestedFiles.filter(
      (item: RequestedFileExtended) =>
        item.is_uploaded
        || item.is_fulfilled
        || item.fulfillment_status === 'not_applicable'
        || item.fulfillment_status === 'not_available'
    ).length;
    setProgress((answered / requestedFiles.length) * 100);
    setDocumentsToShow(requestedFiles);
  }, [requestedFiles, uploadedFiles, companyDocuments]);

  useEffect(() => {
    if (task.task.is_done) {
      setShowUploadBox(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.task.is_done]);

  const documentUploadsProps = {
    requestedFiles,
    isUploading,
    setOpenSelectionModal,
    progress,
    openSelectionModal,
    filesToUpload,
    setFilesToUpload,
    uploadToOtherRequestedFile,
    documentsToShow,
    companyDocuments,
    showUploadBox,
    setShowUploadBox,
    pairedQuestionnaire,
    setUploadedFiles,
  };

  if (!task.task) {
    return null;
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ProcessManagementRequestHeader
            title="Documents"
            subtitle="Documents requested"
            requestedTasksType="documents"
          />
        </Grid>
        {pairedQuestionnaire === null ? (
          <>
            <Grid size={{ xs: 6 }}>
              <DocumentUploadModal {...documentUploadsProps} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <ProcessManagementDocumentList
                documentsToShow={documentsToShow}
                setUploadedFiles={setUploadedFiles}
                companyDocuments={companyDocuments}
              />
            </Grid>
          </>
        ) : (
          <Grid size={{ xs: 12 }}>
            <DocumentUploadModal {...documentUploadsProps} />
            <ProcessManagementDocumentList
              documentsToShow={documentsToShow}
              setUploadedFiles={setUploadedFiles}
              companyDocuments={companyDocuments}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Box display="flex" justifyContent="right" alignItems="center">
            <HowDoIUseThisDocumentsUploadSection />
          </Box>
        </Grid>
      </Grid>
      {openSelectionModal && (
        <SelectionModal
          {...documentUploadsProps}
          uploadRequestedFiles={uploadRequestedFiles}
          open={openSelectionModal}
          setUploadedFiles={setUploadedFiles}
          companyDocuments={Array.isArray(companyDocuments) ? companyDocuments : []}
        />
      )}
    </>
  );
};

export default ProcessManagementDocumentUploads;
